import { AlertTriangle, CalendarClock, Download, FileText, RefreshCw, Users, Banknote, ShoppingCart, Megaphone } from 'lucide-react';
import { DataTable, NoOpsSelected, PageError, PageLoading } from '../../../../components';
import { Alert, AlertDescription, AlertTitle } from '../../../../components/ui/alert';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Tabs, TabsContent, PageTabsList, PageTabsTrigger } from '../../../../components/ui/tabs';
import { formatCurrencyBrlFromCents, formatDateIso } from '../../../../utils';
import { useIndriveFechamento } from './hooks/use-indrive-fechamento';

export function IndriveFechamento() {
  const {
    currentOps,
    data,
    summaryStats,
    loading,
    isFetching,
    error,
    vencimentos,
    vencimento,
    setSelectedSexta,
    isOpenCycle,
    resumoColumns,
    operacoesColumns,
    campanhasColumns,
    handleDownloadResumo,
    handleDownloadOperacoes,
    handleDownloadCampanhas,
    loadData,
  } = useIndriveFechamento();

  // Hoje em GMT-3 (para marcar vencimentos futuros como "ciclo atual").
  const todayStr = new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString().slice(0, 10);

  if (!currentOps) {
    return <NoOpsSelected message="Selecione uma operação para visualizar o fechamento." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-[hsl(210,50%,10%)]">Fechamento InDrive</h1>
          <p className="text-sm text-[hsl(215,16%,47%)]">
            Valor devido por vencimento —{' '}
            <span className="font-medium text-[hsl(215,16%,30%)]">{currentOps}</span>
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadData}
          disabled={isFetching}
          className="flex items-center gap-2 shrink-0 self-start"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Seletor de vencimento (sexta-feira) */}
      <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-[hsl(210,50%,10%)]">
              <CalendarClock className="h-4 w-4 text-[hsl(215,16%,47%)]" />
              Vencimento:
            </div>
            <Select value={vencimento} onValueChange={setSelectedSexta}>
              <SelectTrigger className="w-full sm:w-72 border-[hsl(214,32%,91%)]">
                <SelectValue placeholder="Selecione um vencimento" />
              </SelectTrigger>
              <SelectContent>
                {vencimentos.map((v) => {
                  const open = v >= todayStr;
                  return (
                    <SelectItem key={v} value={v}>
                      <span className={open ? 'font-semibold text-amber-700' : ''}>
                        {formatDateIso(v)}{open ? ' — ciclo atual' : ''}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          {vencimento && (
            <p className="mt-2 text-xs text-[hsl(215,16%,47%)]">
              Pagamentos com vencimento em{' '}
              <span className="font-medium">{formatDateIso(vencimento)}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {isOpenCycle && (
        <Alert className="border-amber-400 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800 font-semibold">Ciclo em aberto — dados parciais</AlertTitle>
          <AlertDescription className="text-amber-700">
            O vencimento ainda não ocorreu. Novos bônus podem ser adicionados a este ciclo.
          </AlertDescription>
        </Alert>
      )}

      <PageError error={error} />
      <PageLoading loading={loading} />

      {!loading && data && (
        <>
          {summaryStats && (
            <>
              {/* Resumo dos pagamentos do vencimento */}
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-50">
                        <Users className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">Motoristas</p>
                        <p className="text-2xl font-bold text-[hsl(210,50%,10%)]">{summaryStats.totalMotoristas}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-violet-50">
                        <FileText className="h-4 w-4 text-violet-600" />
                      </div>
                      <div>
                        <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">Pagamentos</p>
                        <p className="text-2xl font-bold text-[hsl(210,50%,10%)]">{summaryStats.totalPagamentos}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-amber-50">
                        <Banknote className="h-4 w-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">Valor Face Total</p>
                        <p className="text-lg font-bold text-amber-700 leading-tight">
                          {formatCurrencyBrlFromCents(summaryStats.valorFaceTotal)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-indigo-50">
                        <ShoppingCart className="h-4 w-4 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">Valor Compra Total</p>
                        <p className="text-lg font-bold text-indigo-700 leading-tight">
                          {formatCurrencyBrlFromCents(summaryStats.valorCompraTotal)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          <Tabs defaultValue="resumo" className="w-full">
            <PageTabsList>
              <PageTabsTrigger value="resumo">
                <Users className="h-4 w-4" />
                Resumo por Motorista
                {data.resumo.length > 0 && (
                  <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs min-w-[20px] text-center">
                    {data.resumo.length}
                  </Badge>
                )}
              </PageTabsTrigger>
              <PageTabsTrigger value="operacoes">
                <FileText className="h-4 w-4" />
                Operações
                {data.operacoes.length > 0 && (
                  <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs min-w-[20px] text-center">
                    {data.operacoes.length}
                  </Badge>
                )}
              </PageTabsTrigger>
              <PageTabsTrigger value="campanhas">
                <Megaphone className="h-4 w-4" />
                Pagamentos por Campanha
                {(data.porCampanha?.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs min-w-[20px] text-center">
                    {data.porCampanha.length}
                  </Badge>
                )}
              </PageTabsTrigger>
            </PageTabsList>

            <TabsContent value="resumo" className="mt-4">
              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-[hsl(214,32%,91%)]">
                  <div>
                    <CardTitle className="text-base font-semibold text-[hsl(210,50%,10%)]">
                      Resumo agregado por motorista
                    </CardTitle>
                    <p className="text-xs text-[hsl(215,16%,47%)] mt-0.5">
                      Valor total devido por motorista para este vencimento
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadResumo}
                    disabled={data.resumo.length === 0}
                    className="flex items-center gap-2 shrink-0"
                  >
                    <Download className="h-4 w-4" />
                    Exportar CSV
                  </Button>
                </CardHeader>
                <CardContent className="pt-4">
                  {data.resumo.length === 0 ? (
                    <p className="text-center text-[hsl(215,16%,47%)] py-10">Nenhum dado para este vencimento.</p>
                  ) : (
                    <DataTable
                      data={data.resumo}
                      columns={resumoColumns}
                      searchPlaceholder="Buscar por CPF ou nome..."
                      pageSize={50}
                      compact
                      enableExport={false}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="operacoes" className="mt-4">
              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-[hsl(214,32%,91%)]">
                  <div>
                    <CardTitle className="text-base font-semibold text-[hsl(210,50%,10%)]">
                      Operações individuais
                    </CardTitle>
                    <p className="text-xs text-[hsl(215,16%,47%)] mt-0.5">
                      Cada linha representa um bônus processado com vencimento nesta sexta
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadOperacoes}
                    disabled={data.operacoes.length === 0}
                    className="flex items-center gap-2 shrink-0"
                  >
                    <Download className="h-4 w-4" />
                    Exportar CSV
                  </Button>
                </CardHeader>
                <CardContent className="pt-4">
                  {data.operacoes.length === 0 ? (
                    <p className="text-center text-[hsl(215,16%,47%)] py-10">Nenhuma operação encontrada.</p>
                  ) : (
                    <DataTable
                      data={data.operacoes}
                      columns={operacoesColumns}
                      searchPlaceholder="Buscar por CPF ou nome..."
                      pageSize={100}
                      compact
                      enableExport={false}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="campanhas" className="mt-4">
              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-[hsl(214,32%,91%)]">
                  <div>
                    <CardTitle className="text-base font-semibold text-[hsl(210,50%,10%)]">
                      Pagamentos por campanha
                    </CardTitle>
                    <p className="text-xs text-[hsl(215,16%,47%)] mt-0.5">
                      Pagamentos comprados ou liquidados agregados por campanha neste vencimento
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadCampanhas}
                    disabled={(data.porCampanha?.length ?? 0) === 0}
                    className="flex items-center gap-2 shrink-0"
                  >
                    <Download className="h-4 w-4" />
                    Exportar CSV
                  </Button>
                </CardHeader>
                <CardContent className="pt-4">
                  {(data.porCampanha?.length ?? 0) === 0 ? (
                    <p className="text-center text-[hsl(215,16%,47%)] py-10">Nenhum pagamento por campanha neste vencimento.</p>
                  ) : (
                    <DataTable
                      data={data.porCampanha}
                      columns={campanhasColumns}
                      searchPlaceholder="Buscar por campanha..."
                      pageSize={50}
                      compact
                      enableExport={false}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
